import React, { useEffect, useState } from "react";

// @mui material components
import { Box, Grid, IconButton, TextField } from "@mui/material";
import Icon from "@mui/material/Icon";

import { DrawerForm, DrawerFormAddButton, DrawerFormSection } from "../../../../components/DrawerForm";
import { FormHiddenField } from "../../../../components/forms";
import { DRAWER_FORM_GRID_SPACING, drawerReadonlyFieldSx } from "../../../../lib/ui/dialogFormLayout";
import { useTranslation } from "react-i18next";

const EditIntentRuleDialogBody = (item: any) => {
  const { t } = useTranslation();
  const [examples, setExamples] = useState<string[]>([]);
  const [dslHintText, setDslHintText] = useState<string>("{}");
  const [dslHintError, setDslHintError] = useState<string | null>(null);

  useEffect(() => {
    const initialExamples = Array.isArray(item?.userQueryExamples)
      ? item.userQueryExamples
      : [];
    setExamples(initialExamples.length > 0 ? initialExamples : [""]);

    const initialDsl =
      item?.dslHint && typeof item.dslHint === "object"
        ? JSON.stringify(item.dslHint, null, 2)
        : String(item?.dslHint ?? "{}");
    setDslHintText(initialDsl);
    try {
      JSON.parse(initialDsl.trim() || "{}");
      setDslHintError(null);
    } catch {
      setDslHintError("Invalid JSON format.");
    }
  }, [item]);

  const setExampleAt = (index: number, value: string) => {
    setExamples((prev) => prev.map((x, i) => (i === index ? value : x)));
  };

  const addExample = () => setExamples((prev) => [...prev, ""]);

  const removeExample = (index: number) => {
    setExamples((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length === 0 ? [""] : next;
    });
  };

  const handleDslHintChange = (value: string) => {
    setDslHintText(value);
    const v = value.trim();
    if (!v) {
      setDslHintError("dslHint cannot be empty.");
      return;
    }
    try {
      JSON.parse(v);
      setDslHintError(null);
    } catch {
      setDslHintError("Invalid JSON format.");
    }
  };

  const hiddenUserQueryExamples = examples.join("\n");

  return (
    <DrawerForm>
      <DrawerFormSection title={t("form.intent")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={12}>
            <TextField
              fullWidth
              id="id"
              name="id"
              label={t("common.id")}
              variant="outlined"
              size="small"
              value={item?.id || ""}
              InputProps={{ readOnly: true }}
              sx={drawerReadonlyFieldSx}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              id="intent"
              name="intent"
              label={t("intentRules.intent")}
              variant="outlined"
              size="small"
              defaultValue={item?.intent || ""}
            />
          </Grid>
        </Grid>
      </DrawerFormSection>

      <DrawerFormSection title={t("intentRules.examples")} action={<DrawerFormAddButton onClick={addExample} label={t("common.add")} />}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {examples.map((ex, i) => (
            <Box key={i} sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
              <TextField
                fullWidth
                required={i === 0}
                value={ex}
                onChange={(e) => setExampleAt(i, e.target.value)}
                variant="outlined"
                size="small"
                placeholder={`Example ${i + 1}`}
              />
              {examples.length > 1 && i > 0 ? (
                <IconButton onClick={() => removeExample(i)} size="small" aria-label={t("intentRules.removeExample")}>
                  <Icon>remove</Icon>
                </IconButton>
              ) : null}
            </Box>
          ))}
        </Box>
        <FormHiddenField name="userQueryExamples" value={hiddenUserQueryExamples} />
      </DrawerFormSection>

      <DrawerFormSection title={t("form.analysisRule")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              id="targetIndex"
              name="targetIndex"
              label={t("intentRules.targetIndex")}
              size="small"
              defaultValue={item?.analysisRule?.targetIndex || ""}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              required
              fullWidth
              id="filterField"
              name="filterField"
              label={t("intentRules.filterField")}
              size="small"
              defaultValue={item?.analysisRule?.filterField || ""}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              required
              fullWidth
              id="searchType"
              name="searchType"
              label={t("intentRules.searchType")}
              size="small"
              defaultValue={item?.analysisRule?.searchType || ""}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              id="searchValue"
              name="searchValue"
              label={t("intentRules.searchValue")}
              size="small"
              defaultValue={item?.analysisRule?.searchValue || ""}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              required
              fullWidth
              id="timeField"
              name="timeField"
              label={t("intentRules.timeField")}
              size="small"
              defaultValue={item?.analysisRule?.timeField || ""}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              required
              fullWidth
              id="timeRange"
              name="timeRange"
              label={t("intentRules.timeRange")}
              size="small"
              defaultValue={item?.analysisRule?.timeRange || ""}
              placeholder="24h / 7d"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              id="aggregationType"
              name="aggregationType"
              label={t("intentRules.aggregation")}
              size="small"
              defaultValue={item?.analysisRule?.aggregationType || ""}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              id="dslHint"
              name="dslHint"
              label={t("intentRules.dslHint")}
              size="small"
              value={dslHintText}
              onChange={(e) => handleDslHintChange(e.target.value)}
              placeholder='{"field":"value"}'
              error={!!dslHintError}
              helperText={dslHintError ?? "Valid JSON"}
            />
          </Grid>
        </Grid>
      </DrawerFormSection>
    </DrawerForm>
  );
};

export default EditIntentRuleDialogBody;
